<template>
<div>
    <div class='h-text-center h-mb-20'>
        <div class='b-base-title h-mt-10'>
            {{ $t('AUTH.LOGIN.TITLE') }}
        </div>
    </div>
    <div>
        <form class='h-mt-15'
              @keyup.enter='sendDataToServer'>
            <FwFormInput
                class='h-mb-25 qa-user-email-input'
                :fieldHasError='fieldHasErrorByRule(email, EMAIL, emailRules)'
                labelType='label-thin'
                :title='$t(`AUTH.SIGN_UP.FORM.EMAIL.LABEL`)'>
                <FwInput
                    v-focus='true'
                    :value='email'
                    type='text'
                    name='text'
                    inputStyle='gray'
                    placeholder='adresse@email.com'
                    :error='getFieldErrorTextByRule(email, EMAIL, emailRules)'
                    @update='value => email = value'
                    @input='clearServerErrorsBase(EMAIL)'
                    @blur='addCurrentInputToValidateArray(EMAIL)'>
                </FwInput>
            </FwFormInput>
            <div class='h-pos-rel'>
                <router-link
                    :to='{ name: "ForgotPasswordPopup" }'
                    class='b-login-form-forgot-password'>
                    {{ $t(`AUTH.LOGIN.FORM.FORGET.PASSWORD`) }}
                </router-link>
                <FwFormInput
                    class='h-mb-25 qa-user-password-input'
                    :fieldHasError='fieldHasErrorByRule(password, PASSWORD, passwordRules)'
                    labelType='label-thin'
                    :title='$t(`AUTH.LOGIN.FORM.PASSWORD.LABEL`)'>
                    <FwInput
                        :value='password'
                        type='password'
                        name='password'
                        :placeholder='$t(`AUTH.LOGIN.FORM.PASSWORD.PLACEHOLDER`)'
                        inputStyle='gray'
                        :error='getFieldErrorTextByRule(password, PASSWORD, passwordRules)'
                        @update='value => password = value'
                        @input='clearServerErrorsBase(PASSWORD)'
                        @blur='addCurrentInputToValidateArray(PASSWORD)'>
                    </FwInput>
                </FwFormInput>
            </div>
        </form>
    </div>
    <div class='b-text-error h-text-center h-fw-500'>
        {{ globalServerError }}
    </div>
    <div class='h-flex h-flex-center h-flex-dir-column h-width-100p'>
        <GoogleSignIn
            v-if='isHesAccount'
            :account='companyName.toLowerCase()'></GoogleSignIn>
        <a
            v-if='isSsoShown'
            class='b-login-sso--button'
            :href='accountSsoUrl'>
            <div class='h-mb-10 h-pointer h-fw-500 h-text-center'>
                {{ $t('LOGIN.WITH.SSO') }}
            </div>
        </a>
        <FwButton
            class='h-mt-10 qa-base-login-submit'
            :disabled='!isFormValid || isRequestSend'
            borderRadiusType='small-border'
            @click='sendDataToServer'>
            {{ $t('AUTH.LOGIN.FORM.BUTTON.NEXT') }}
        </FwButton>
    </div>
    <router-link
        :to='{ name: "ConfirmMyAccount" }'
        class='b-url-with-arrow h-mt-20'>
        {{ $t('AUTH.CONFIRM_ACCOUNT.LINK_TEXT') }}
        <FwIcon
            class='h-ml-15 b-url-with-arrow__icon'
            icon='arrow-right'
            size='10'
            color='#BEC7D3'>
        </FwIcon>
    </router-link>
</div>
</template>

<script lang='ts'>
import { Component, Emit, mixins, Prop } from 'vue-facing-decorator';

import { EMAIL, PASSWORD } from '@/helpers/auth';

import { LoginPayloadType } from '@/types/Auth';

import { GoogleSignIn } from '@/components/common/GoogleSignIn';

import { useValidation } from '@/composables/useValidation';
import AuthMixin from '@/mixins/auth';
import { useUserStore } from '@/stores/useUserStore';

@Component({
    components: {
        GoogleSignIn,
    },
})
export default class LogInForm extends mixins(AuthMixin) {
    @Prop({ type: String, required: true }) readonly globalServerError!: string;
    @Prop({ type: Boolean, required: true }) readonly isRequestSend!: boolean;

    [EMAIL]: string = '';
    [PASSWORD]: string = '';
    EMAIL: string = EMAIL;
    PASSWORD: string = PASSWORD;
    emailRules: string[] = ['required', 'email'];
    passwordRules: string[] = ['required'];

    validation = useValidation();

    get companyName() {
        const userStore = useUserStore();
        return userStore.companyName.toLowerCase();
    }

    get fieldHasErrorByRule() {
        return this.validation.fieldHasErrorByRule;
    }

    get getFieldErrorTextByRule() {
        return this.validation.getFieldErrorTextByRule;
    }

    get clearServerErrorsBase() {
        return this.validation.clearServerErrorsBase;
    }

    get addCurrentInputToValidateArray() {
        return this.validation.addCurrentInputToValidateArray;
    }

    get isFormValid() {
        return this.validation.emailValidator({ value: this[EMAIL] }) &&
            this.validation.requiredValidator({ value: this[PASSWORD] });
    }

    get isSsoShown() {
        return ['matmut', 'mnh', 'alm', 'fortuneo_accompagnement', 'fortuneo_lab_immo', 'fortuneo_patrimoine'].includes(this.companyName.toLowerCase());
    }

    get isHesAccount() {
        return this.companyName === 'hes';
    }

    get ssoUrl() {
        return process.env.NODE_ENV === 'production' ?
            'https://backend.calizy.com' :
            'https://backend.staging.calizy.com';
    }

    get accountSsoUrl() {
        if (['mnh', 'alm', 'fortuneo_accompagnement', 'fortuneo_lab_immo', 'fortuneo_patrimoine'].includes(this.companyName.toLowerCase())) {
            return `${this.ssoUrl}/saml_pro/login?a=${this.companyName}`;
        }

        return `${this.ssoUrl}/saml_pro/login`;
    }

    @Emit('submitForm')
    sendDataToServer(): LoginPayloadType {
        return {
            [EMAIL]: this[EMAIL],
            [PASSWORD]: this[PASSWORD],
        };
    }
}
</script>

<style lang='sass'>
.b-login-sso--button
    &:hover
        text-decoration: underline
.b-login-form-forgot-password
    position: absolute
    right: 0
    top: 0
    font-size: 14px
    font-weight: 500
    color: $main-green
</style>
